import { copyRoute, useRouteSizes } from '../data/routeAssist';
import { loadStoreModel } from '../data/shopping';
import { useStores, type StoreView } from '../data/stores';
import { plural } from './shop/status';
import { StorePicker } from './StorePicker';

interface Props {
  /** Sklep, do którego kopiujemy trasę. */
  target: StoreView | null;
  open: boolean;
  onClose: () => void;
  /** Komunikat po próbie kopiowania (do toastu). */
  onDone: (message: string) => void;
}

const lower = (s: string | null | undefined) => s?.trim().toLocaleLowerCase('pl-PL') || null;
const produkty = (n: number) => `${n} ${plural(n, 'produkt', 'produkty', 'produktów')}`;

/** „Skopiuj trasę z innego sklepu…”: wybór źródła (najpierw ta sama sieć, z wielkością trasy) i kopia. */
export function CopyRoutePicker({ target, open, onClose, onDone }: Props) {
  const stores = useStores();
  const sizes = useRouteSizes(open);
  const chain = lower(target?.chainName);
  const dst = target?.name || 'Bez nazwy';

  const hint = (s: StoreView) => {
    if (!sizes) return null;
    const r = sizes.get(s.id);
    const parts = [r?.confirmed ? `trasa: ${produkty(r.confirmed)}` : 'bez trasy'];
    if (r?.visits) parts.push(`${r.visits} ${plural(r.visits, 'wizyta', 'wizyty', 'wizyt')}`);
    return parts.join(' · ');
  };

  const copy = async (sourceId: string) => {
    if (!target) return;
    const src = stores?.find((s) => s.id === sourceId)?.name || 'Bez nazwy';
    try {
      // Ręczna kopia zastępuje trasę (reset), której nie da się odtworzyć — bez „Cofnij”, więc pytamy.
      const known = Object.keys((await loadStoreModel(target.id)).byProduct).length;
      if (known > 0 && !confirm(`Skopiować trasę z „${src}” do „${dst}”?\n\nObecna trasa zostanie zastąpiona.`)) return;
      const n = await copyRoute(target.id, sourceId);
      onDone(n > 0 ? `Skopiowano trasę z „${src}” (${produkty(n)}).` : `„${src}” nie ma jeszcze trasy do skopiowania.`);
    } catch {
      onDone('Nie udało się skopiować trasy.');
    }
  };

  return (
    <StorePicker
      open={open}
      onClose={onClose}
      onPick={(id) => void copy(id)}
      title={`Skopiuj trasę do „${dst}” z…`}
      excludeId={target?.id ?? null}
      allowAdd={false}
      prioritize={chain ? (s) => lower(s.chainName) === chain : undefined}
      hint={hint}
    />
  );
}
